import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faTimes, faQuestionCircle,
  faExclamationTriangle, faSort, faSortUp, faSortDown, faUndo,
  faRedo, faCrosshairs
} from '@fortawesome/free-solid-svg-icons';
import AuthSmashMatrixFilters from './AuthSmashMatrixFilters';
import ByteComparison from './ByteComparison';

const STORAGE_KEY = 'authsmash-matrix-filters';
const DEFAULT_FILTERS = {
  sortBy: 'url',
  sortDesc: false,
  filterSeverity: 'all',
  excludeStatic: true,
  enforcement: [], // ['enforced', 'bypassed', 'partial']
  methods: [], // ['GET', 'POST', etc]
  contentTypes: [], // ['json', 'html', 'xml', etc]
  searchText: '' // fulltext search in URL, request, response
};

// Compact column label from a long profile name/email (full name shown on hover).
const shortProfileName = (name) => {
  if (!name) return '';
  if (/replayed here/i.test(name)) return 'replayed ↻';
  let n = String(name).replace(/^Auto:\s*/i, '').replace(/^HAR capture \(device\):?\s*/i, '');
  const em = n.match(/(?:\+([^@\s]+)|([a-z0-9._-]+))@[a-z0-9.\-]+/i);
  if (em) return em[1] || em[2];
  return n.length > 14 ? n.slice(0, 13) + '…' : n;
};

const AuthSmashMatrixTable = ({
  matrix,           // { scenario, baseline, profiles, endpoints }
  onCellClick,      // (endpoint, profile, cell) => void
  onReload,         // () => void — re-fetch the matrix after a re-baseline
  onSendToRepeater, // (exchangeId) => void — send a cell's exchange to the Repeater tab
  onSendToIntruder, // (exchangeId) => void — send a cell's exchange to the Intruder tab
  isDarkMode
}) => {
  // ─── Right-click context menu ────────────────────────────────────────
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, exchangeId, label }
  const ctxRef = useRef(null);

  // Close on outside click or Escape
  useEffect(() => {
    if (!ctxMenu) return;
    const close = (e) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target)) setCtxMenu(null);
    };
    const esc = (e) => { if (e.key === 'Escape') setCtxMenu(null); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [ctxMenu]);

  const handleCellContext = useCallback((e, cell, label) => {
    if (!cell?.exchangeId) return;
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, exchangeId: cell.exchangeId, label });
  }, []);
  const [rebaselining, setRebaselining] = useState(false);
  const handleSetBaseline = async (referenceId) => {
    if (!referenceId || String(referenceId) === String(matrix?.baseline?._id)) return;
    setRebaselining(true);
    try {
      const authSmashService = (await import('../../services/authSmashService')).default;
      await authSmashService.setBaseline(matrix?.scenario?._id || matrix?.scenario, referenceId);
      onReload?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert('Re-baseline failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setRebaselining(false);
    }
  };
  // Load filters from localStorage or use defaults
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : DEFAULT_FILTERS;
    } catch {
      return DEFAULT_FILTERS;
    }
  });

  const { sortBy, sortDesc, filterSeverity, excludeStatic, searchText } = filters;

  // Server-side content search (bodies aren't shipped with the matrix).
  // contentMatches: Map<exchangeId, 'request'|'response'|'both'> or null when not searching.
  const scenarioId = matrix?.scenario?._id || matrix?.scenario;
  const [contentMatches, setContentMatches] = useState(null);
  const [contentSearching, setContentSearching] = useState(false);
  useEffect(() => {
    const q = (searchText || '').trim();
    if (!scenarioId || q.length < 2) { setContentMatches(null); setContentSearching(false); return; }
    let cancelled = false;
    setContentSearching(true);
    const t = setTimeout(async () => {
      try {
        const authSmashService = (await import('../../services/authSmashService')).default;
        const res = await authSmashService.searchBodies(scenarioId, q);
        if (!cancelled) {
          // Build Map<id, location> from the backend's matchLocations object.
          const locs = res.matchLocations || {};
          const map = new Map();
          for (const id of (res.matchIds || [])) map.set(String(id), locs[id] || 'response');
          setContentMatches(map);
        }
      } catch { if (!cancelled) setContentMatches(new Map()); }
      finally { if (!cancelled) setContentSearching(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchText, scenarioId]);

  // Which individual cells contain the search term (by exchange ID), and WHERE
  // the match lives (request, response, or both) for color-coded highlights.
  const searchActive = (searchText || '').trim().length >= 2;
  const cellHit = (cell) => searchActive && contentMatches && cell?.exchangeId && contentMatches.has(String(cell.exchangeId));
  const cellHitLocation = (cell) => contentMatches?.get(String(cell?.exchangeId)); // 'request'|'response'|'both'|undefined
  const glowClass = (cell) => {
    const loc = cellHitLocation(cell);
    if (!loc) return '';
    return loc === 'both' ? 'authsmash-search-glow-both' : loc === 'request' ? 'authsmash-search-glow-request' : 'authsmash-search-glow-response';
  };
  const glowLabel = (cell) => {
    const loc = cellHitLocation(cell);
    if (!loc) return '';
    return loc === 'both' ? 'in request & response' : loc === 'request' ? 'in request body' : 'in response body';
  };

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  // Check if URL is a static file - MUST be before useMemo hooks
  const isStaticFile = (url) => {
    const staticExtensions = /\.(css|js|jsx|jsdbx|assetx|jpg|jpeg|png|gif|svg|webp|ico|woff|woff2|ttf|eot|otf|mp4|webm|mp3|wav|pdf|zip|gz|tar)(\?|$)/i;
    return staticExtensions.test(url);
  };

  // Determine content type category from URL or content-type header
  const getContentType = (url) => {
    if (!url) return 'other';
    const lower = url.toLowerCase();

    if (lower.includes('/json') || lower.endsWith('.json')) return 'json';
    if (lower.includes('/xml') || lower.endsWith('.xml')) return 'xml';
    if (lower.includes('/html') || lower.endsWith('.html') || lower.endsWith('.php')) return 'html';
    if (lower.match(/\.(jpg|jpeg|png|gif|svg|webp|ico)/)) return 'image';
    if (lower.includes('/text') || lower.endsWith('.txt')) return 'text';

    return 'other';
  };

  // Sort endpoints - MUST be before early return (hooks rule)
  const sortedEndpoints = useMemo(() => {
    if (!matrix?.endpoints) return [];
    return [...matrix.endpoints].sort((a, b) => {
      if (sortBy === 'url') {
        return sortDesc
          ? (b.url || '').localeCompare(a.url || '')
          : (a.url || '').localeCompare(b.url || '');
      } else if (sortBy === 'method') {
        return sortDesc
          ? (b.method || '').localeCompare(a.method || '')
          : (a.method || '').localeCompare(b.method || '');
      } else if (sortBy === 'firstSeen') {
        const aTime = a.firstSeen ? new Date(a.firstSeen).getTime() : 0;
        const bTime = b.firstSeen ? new Date(b.firstSeen).getTime() : 0;
        return sortDesc ? bTime - aTime : aTime - bTime;
      } else if (sortBy === 'lastSeen') {
        const aTime = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
        const bTime = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
        return sortDesc ? bTime - aTime : aTime - bTime;
      }
      return 0;
    });
  }, [matrix?.endpoints, sortBy, sortDesc]);

  // Filter endpoints with ALL filter types
  const filteredEndpoints = useMemo(() => {
    return sortedEndpoints.filter(ep => {
      if (!ep) return false;

      // Exclude static files if enabled
      if (excludeStatic && ep.url && isStaticFile(ep.url)) {
        return false;
      }

      // Apply fulltext search filter
      if (searchText && searchText.trim()) {
        const searchLower = searchText.toLowerCase().trim();
        let matchFound = false;

        // Search in URL (including hostname)
        if (ep.url && ep.url.toLowerCase().includes(searchLower)) {
          matchFound = true;
        }

        // Content search runs server-side (matrix ships snippets, not full bodies).
        // contentMatches holds the exchange IDs whose request/response body matched;
        // an endpoint matches if any of its cells' exchange is in that set.
        if (!matchFound && contentMatches) {
          const ids = [
            ep.baseline?.exchangeId,
            ep.baselineExchangeId,
            ep.unauthenticated?.exchangeId,
            ...Object.values(ep.profiles || {}).map(c => c?.exchangeId)
          ].filter(Boolean).map(String);
          matchFound = ids.some(id => contentMatches.has(id));
        }

        if (!matchFound) return false;
      }

      // Apply severity filter (old way)
      if (filterSeverity !== 'all') {
        const hasMatch = Object.values(ep.profiles || {}).some(p => p?.enforcement === filterSeverity) ||
               (ep.unauthenticated && ep.unauthenticated.enforcement === filterSeverity);
        if (!hasMatch) return false;
      }

      // Apply NEW enforcement filters (if any selected)
      if (filters.enforcement && filters.enforcement.length > 0) {
        const hasEnforcementMatch = Object.values(ep.profiles || {}).some(p =>
          p?.enforcement && filters.enforcement.includes(p.enforcement)
        ) || (ep.unauthenticated && filters.enforcement.includes(ep.unauthenticated.enforcement));

        if (!hasEnforcementMatch) return false;
      }

      // Apply method filter
      if (filters.methods && filters.methods.length > 0) {
        if (!filters.methods.includes(ep.method)) return false;
      }

      // Apply content type filter
      if (filters.contentTypes && filters.contentTypes.length > 0) {
        const epContentType = getContentType(ep.url);
        if (!filters.contentTypes.includes(epContentType)) return false;
      }

      return true;
    });
  }, [sortedEndpoints, excludeStatic, filterSeverity, filters.enforcement, filters.methods, filters.contentTypes, searchText, contentMatches]);

  // Calculate counts for filter badges from SCENARIO SUMMARY
  const filterCounts = useMemo(() => {
    const counts = {
      enforced: 0,
      bypassed: 0,
      partial: 0,
      methods: {},
      contentTypes: {}
    };

    // Count UNIQUE ENDPOINTS by their enforcement status (not total test executions)
    sortedEndpoints.forEach(ep => {
      // Determine enforcement status for this endpoint by checking all profiles
      let hasEnforced = false;
      let hasBypassed = false;
      let hasPartial = false;

      // Check all test profiles and unauthenticated
      Object.values(ep.profiles || {}).forEach(profile => {
        if (profile?.enforcement === 'enforced') hasEnforced = true;
        if (profile?.enforcement === 'bypassed') hasBypassed = true;
        if (profile?.enforcement === 'partial') hasPartial = true;
      });

      if (ep.unauthenticated) {
        if (ep.unauthenticated.enforcement === 'enforced') hasEnforced = true;
        if (ep.unauthenticated.enforcement === 'bypassed') hasBypassed = true;
        if (ep.unauthenticated.enforcement === 'partial') hasPartial = true;
      }

      // Count endpoint based on highest severity (bypassed > partial > enforced)
      if (hasBypassed) {
        counts.bypassed++;
      } else if (hasPartial) {
        counts.partial++;
      } else if (hasEnforced) {
        counts.enforced++;
      }

      // Count methods and content types
      counts.methods[ep.method] = (counts.methods[ep.method] || 0) + 1;
      const ct = getContentType(ep.url);
      counts.contentTypes[ct] = (counts.contentTypes[ct] || 0) + 1;
    });

    return counts;
  }, [sortedEndpoints]);

  // Filter change handler
  const handleFilterChange = (key, value) => {
    if (key === 'reset') {
      setFilters(DEFAULT_FILTERS);
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  // Early return check - AFTER all hooks
  if (!matrix || !matrix.endpoints) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        No matrix data available
      </div>
    );
  }

  // Check if profiles have been deleted
  if (!matrix.baseline || !matrix.profiles || matrix.profiles.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center h-full p-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-3xl mb-3 text-amber-500" />
        <div className="text-sm font-medium mb-2">Profiles Deleted</div>
        <div className="text-xs max-w-md">
          The authentication profiles used in this test have been deleted.
          Please run a new AuthSmash test with current profiles to see the matrix view.
        </div>
        <div className="text-[10px] mt-3 opacity-75">
          {matrix.endpoints?.length || 0} endpoints were tested in this scenario
        </div>
      </div>
    );
  }

  // Test-profile columns, excluding the baseline (it already has its own column).
  const testProfiles = (matrix.profiles || []).filter(
    p => String(p._id) !== String(matrix.baseline?._id)
  );

  // Rich hover tooltip: how this cell's response differs from the baseline.
  const buildDiffTooltip = (profile, cell, base, baselineProfile) => {
    const bStatus = base?.statusCode ?? '—';
    const bLen = base?.contentLength ?? 0;
    const tLen = cell.contentLength ?? 0;
    const dLen = tLen - bLen;
    const pct = bLen ? Math.round((dLen / bLen) * 100) : (tLen ? 100 : 0);
    const sign = dLen > 0 ? '+' : '';
    const lines = [
      `${profile.name}  vs  baseline (${baselineProfile?.name || 'baseline'})`,
      `Status:  ${bStatus} → ${cell.statusCode ?? '—'}   ${bStatus === cell.statusCode ? '(same)' : '(differs)'}`,
      `Size:    ${bLen}B → ${tLen}B   (${sign}${dLen}B, ${sign}${pct}%)`,
      `Verdict: ${cell.enforcement || '—'}${cell.score != null ? `   score ${cell.score}` : ''}`
    ];
    if (cell.reason) lines.push(`Verdict reason: ${cell.reason}`);
    if (cell.snippet?.text) {
      const label = cell.snippet.kind === 'identity' ? '⚠ user identity in response'
        : cell.snippet.kind === 'diff' ? 'first difference vs baseline'
        : 'response head';
      lines.push('');
      lines.push(`${label}:`);
      lines.push(cell.snippet.text);
    }
    lines.push('');
    lines.push('— click for full request/response diff —');
    return lines.join('\n');
  };

  // Get enforcement icon for a cell
  const getEnforcementIcon = (cell) => {
    if (!cell) return <span className="text-gray-500 text-xs">-</span>;

    const { enforcement } = cell;

    if (enforcement === 'enforced') {
      // Access denied - Green X (good)
      return (
        <div className="flex items-center justify-center gap-1">
          <FontAwesomeIcon icon={faTimes} className="text-green-500 text-sm" />
        </div>
      );
    } else if (enforcement === 'bypassed') {
      // Access granted inappropriately - Red checkmark (VULNERABILITY)
      return (
        <div className="flex items-center justify-center gap-1">
          <FontAwesomeIcon icon={faCheckCircle} className="text-red-500 text-sm" />
        </div>
      );
    } else if (enforcement === 'partial') {
      // Needs review - Yellow question mark
      return (
        <div className="flex items-center justify-center gap-1">
          <FontAwesomeIcon icon={faQuestionCircle} className="text-yellow-500 text-sm" />
        </div>
      );
    } else {
      // Error - Gray warning
      return (
        <div className="flex items-center justify-center gap-1">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-500 text-xs" />
        </div>
      );
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      superadmin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      admin: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      manager: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      read_only: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      guest: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      unauthenticated: 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
      custom: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
    };
    return colors[role] || colors.user;
  };

  const getMethodBadgeColor = (method) => {
    const colors = {
      GET: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      POST: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      PUT: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      DELETE: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      PATCH: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      OPTIONS: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      HEAD: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    };
    return colors[method] || 'bg-gray-100 text-gray-700 dark:bg-gray-700';
  };

  // Extract path from full URL
  const getPathFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname + urlObj.search + urlObj.hash;
    } catch {
      return url;
    }
  };

  // Get content type badge info from endpoint
  const getContentTypeBadge = (endpoint) => {
    // Try to infer from URL extension or baseline response
    const url = endpoint.url.toLowerCase();

    if (url.includes('.json') || url.includes('/api/') || url.includes('/v1/') || url.includes('/graphql')) {
      return { label: 'JSON', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' };
    } else if (url.includes('.xml')) {
      return { label: 'XML', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };
    } else if (url.includes('.css')) {
      return { label: 'CSS', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
    } else if (url.includes('.js')) {
      return { label: 'JS', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' };
    } else if (url.match(/\.(jpg|jpeg|png|gif|svg|webp|ico)$/)) {
      return { label: 'IMG', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
    } else if (url.includes('.pdf')) {
      return { label: 'PDF', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };
    } else {
      return { label: 'HTML', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' };
    }
  };


  const handleSort = (field) => {
    if (sortBy === field) {
      updateFilter('sortDesc', !sortDesc);
    } else {
      updateFilter('sortBy', field);
      updateFilter('sortDesc', false);
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return <FontAwesomeIcon icon={faSort} className="text-gray-400 text-xs ml-1" />;
    return sortDesc
      ? <FontAwesomeIcon icon={faSortDown} className="text-blue-400 text-xs ml-1" />
      : <FontAwesomeIcon icon={faSortUp} className="text-blue-400 text-xs ml-1" />;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Neon glow for cells whose request/response contains the search term.
           Orange = response only, Purple = request only, Red = both. */}
      <style>{`
        .authsmash-search-glow-response {
          border-radius: 4px;
          box-shadow:
            0 0 0 2px rgba(250,204,21,0.95),
            0 0 12px 3px rgba(250,204,21,0.85),
            0 0 22px 6px rgba(250,204,21,0.45),
            inset 0 0 10px rgba(250,204,21,0.35);
        }
        .authsmash-search-glow-request {
          border-radius: 4px;
          box-shadow:
            0 0 0 2px rgba(168,85,247,0.95),
            0 0 12px 3px rgba(168,85,247,0.85),
            0 0 22px 6px rgba(168,85,247,0.45),
            inset 0 0 10px rgba(168,85,247,0.35);
        }
        .authsmash-search-glow-both {
          border-radius: 4px;
          box-shadow:
            0 0 0 2px rgba(239,68,68,0.95),
            0 0 12px 3px rgba(239,68,68,0.85),
            0 0 22px 6px rgba(239,68,68,0.45),
            inset 0 0 10px rgba(239,68,68,0.35);
        }
      `}</style>
      {/* Quick Filters */}
      <AuthSmashMatrixFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        counts={filterCounts}
        isDarkMode={isDarkMode}
        excludeStatic={excludeStatic}
        onExcludeStaticChange={(value) => updateFilter('excludeStatic', value)}
        searchText={searchText}
        onSearchChange={(value) => updateFilter('searchText', value)}
        totalEndpoints={matrix.endpoints.length}
        filteredCount={filteredEndpoints.length}
      />

      {/* Re-baseline: recompute all verdicts against any column */}
      <div className={`flex items-center gap-2 px-2 py-1 text-[11px] border-b ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
        <span>Baseline (verdicts computed vs):</span>
        <select
          value={String(matrix?.baseline?._id || '')}
          disabled={rebaselining}
          onChange={e => handleSetBaseline(e.target.value)}
          className={`rounded border px-2 py-0.5 text-[11px] ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
          title="Recompute the whole matrix against the selected column"
        >
          {[matrix.baseline, ...(matrix.profiles || [])].filter(Boolean).map(col => (
            <option key={String(col._id)} value={String(col._id)}>
              {shortProfileName(col.name)}{col.role ? ` (${col.role})` : ''}
            </option>
          ))}
        </select>
        {rebaselining && <FontAwesomeIcon icon={faSort} className="animate-spin" />}
        <span className="opacity-70">— pick any profile or “Unauthenticated” to re-run the comparison; switch back anytime.</span>
      </div>

      {/* Matrix Table */}
      <div className="flex-1 overflow-auto">
        <table className={`w-full text-xs border-collapse ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <thead className={`sticky top-0 z-10 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <tr>
              <th className={`px-3 py-2 text-left border-b w-12 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                #
              </th>
              <th className={`px-3 py-2 text-left border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => handleSort('method')}
                  className="hover:text-blue-400 flex items-center font-semibold"
                >
                  Method
                  {getSortIcon('method')}
                </button>
              </th>
              {/* Baseline column */}
              <th className={`px-1.5 py-1 text-center border-b border-l w-24 max-w-[110px] ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
                <div className="font-semibold text-xs truncate" title={matrix.baseline.name}>{shortProfileName(matrix.baseline.name)}</div>
                <div className={`text-[9px] px-1 py-0.5 rounded inline-block mt-0.5 truncate max-w-full ${getRoleColor(matrix.baseline.role)}`} title="Baseline (device capture)">
                  Baseline
                </div>
              </th>
              {/* Test profile columns (baseline excluded — shown separately) */}
              {testProfiles.map(profile => (
                <th
                  key={profile._id}
                  className={`px-1.5 py-1 text-center border-b border-l w-24 max-w-[110px] ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                >
                  <div className="font-semibold text-xs truncate" title={profile.name}>{shortProfileName(profile.name)}</div>
                  <div className={`text-[9px] px-1 py-0.5 rounded inline-block mt-0.5 truncate max-w-full ${getRoleColor(profile.role)}`} title={profile.role}>
                    {profile.role}
                  </div>
                </th>
              ))}
              {/* Endpoint column - moved to right */}
              <th className={`px-3 py-2 text-left border-b border-l ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => handleSort('url')}
                  className="hover:text-blue-400 flex items-center font-semibold"
                >
                  Endpoint
                  {getSortIcon('url')}
                </button>
              </th>
              {/* First Seen column */}
              <th className={`px-2 py-2 text-center border-b border-l w-28 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => handleSort('firstSeen')}
                  className="hover:text-blue-400 flex items-center font-semibold text-[10px] mx-auto"
                  title="First time this endpoint was requested"
                >
                  First Seen
                  {getSortIcon('firstSeen')}
                </button>
              </th>
              {/* Last Seen column */}
              <th className={`px-2 py-2 text-center border-b border-l w-28 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => handleSort('lastSeen')}
                  className="hover:text-blue-400 flex items-center font-semibold text-[10px] mx-auto"
                  title="Last time this endpoint was requested"
                >
                  Last Seen
                  {getSortIcon('lastSeen')}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredEndpoints.length === 0 ? (
              <tr>
                <td colSpan={6 + testProfiles.length} className={`px-3 py-8 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  No endpoints match the current filter
                </td>
              </tr>
            ) : (
              filteredEndpoints.map((endpoint, idx) => {
                const hasVulnerability = Object.values(endpoint.profiles || {}).some(p => p.enforcement === 'bypassed') ||
                                        (endpoint.unauthenticated && endpoint.unauthenticated.enforcement === 'bypassed');

                return (
                  <tr
                    key={`${endpoint.method}:${endpoint.url}`}
                    className={`border-b ${isDarkMode ? 'border-gray-700 hover:bg-gray-800/50' : 'border-gray-200 hover:bg-gray-50'} ${
                      hasVulnerability ? (isDarkMode ? 'bg-red-900/10' : 'bg-red-50/30') : ''
                    }`}
                  >
                    <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{idx + 1}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${getMethodBadgeColor(endpoint.method)}`}>
                        {endpoint.method}
                      </span>
                    </td>
                    {/* Baseline cell */}
                    <td className={`px-2 py-1.5 text-center border-l cursor-pointer ${isDarkMode ? 'border-gray-700 bg-gray-750/50' : 'border-gray-200 bg-gray-50/50'} ${cellHit(endpoint.baseline) ? glowClass(endpoint.baseline) : ''}`}
                        onContextMenu={(e) => handleCellContext(e, endpoint.baseline, `${endpoint.method} ${endpoint.url} · baseline`)}
                        title={cellHit(endpoint.baseline) ? `"${searchText.trim()}" found ${glowLabel(endpoint.baseline)}` : undefined}>
                      <div className="flex items-center justify-center gap-1.5">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          endpoint.baseline?.statusCode >= 200 && endpoint.baseline?.statusCode < 300
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : endpoint.baseline?.statusCode >= 300 && endpoint.baseline?.statusCode < 400
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {endpoint.baseline?.statusCode || '-'}
                        </span>
                        <span className="text-[9px] text-gray-500">
                          {endpoint.baseline?.contentLength
                            ? endpoint.baseline.contentLength < 1024
                              ? `${endpoint.baseline.contentLength}B`
                              : `${(endpoint.baseline.contentLength / 1024).toFixed(1)}kb`
                            : '-'}
                        </span>
                      </div>
                    </td>
                    {/* Test profile cells (baseline excluded — it has its own column) */}
                    {testProfiles.map(profile => {
                      const cell = profile._id === 'unauth'
                        ? endpoint.unauthenticated
                        : endpoint.profiles[profile._id];
                      const base = endpoint.baseline;
                      const diffTip = cell ? buildDiffTooltip(profile, cell, base, matrix.baseline) : 'No data';

                      return (
                        <td
                          key={profile._id}
                          className={`px-2 py-1.5 text-center border-l cursor-pointer transition-colors ${
                            isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-100'
                          } ${
                            cell?.enforcement === 'bypassed' ? (isDarkMode ? 'bg-red-900/20 hover:bg-red-900/30' : 'bg-red-50 hover:bg-red-100') :
                            cell?.enforcement === 'partial' ? (isDarkMode ? 'bg-yellow-900/20 hover:bg-yellow-900/30' : 'bg-yellow-50 hover:bg-yellow-100') :
                            ''
                          } ${cellHit(cell) ? glowClass(cell) : ''}`}
                          onClick={() => cell && onCellClick(endpoint, profile, cell)}
                          onContextMenu={(e) => handleCellContext(e, cell, `${endpoint.method} ${endpoint.url} · ${profile.name}`)}
                          title={cellHit(cell) ? `"${searchText.trim()}" found ${glowLabel(cell)} — ${diffTip}` : diffTip}
                        >
                          <div className="flex items-center justify-center gap-1.5">
                            <div className="flex-shrink-0">{getEnforcementIcon(cell)}</div>
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              cell?.statusCode >= 200 && cell?.statusCode < 300
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : cell?.statusCode >= 300 && cell?.statusCode < 400
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                                : cell?.statusCode >= 400
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {cell?.statusCode || '-'}
                            </span>
                            <span className="text-[9px] text-gray-500 whitespace-nowrap">
                              {cell?.contentLength
                                ? cell.contentLength < 1024
                                  ? `${cell.contentLength}B`
                                  : `${(cell.contentLength / 1024).toFixed(1)}kb`
                                : '-'}
                              {cell?.contentLength > 0 && endpoint.baseline?.contentLength > 0 && (
                                <ByteComparison
                                  baselineBytes={endpoint.baseline.contentLength}
                                  testBytes={cell.contentLength}
                                />
                              )}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    {/* Endpoint column - moved to right */}
                    <td className="px-3 py-2 border-l">
                      <div className="flex items-center gap-1.5 max-w-[420px]">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium flex-shrink-0 ${getContentTypeBadge(endpoint).color}`}>
                          {getContentTypeBadge(endpoint).label}
                        </span>
                        <span
                          className="font-mono text-[10px] truncate min-w-0"
                          title={endpoint.url}
                        >
                          {getPathFromUrl(endpoint.url)}
                        </span>
                      </div>
                    </td>
                    {/* First Seen cell */}
                    <td className={`px-2 py-2 text-center border-l text-[9px] ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                      {endpoint.firstSeen ? (
                        <div className="flex flex-col items-center">
                          <span>{new Date(endpoint.firstSeen).toLocaleDateString()}</span>
                          <span className="text-[8px] opacity-75">{new Date(endpoint.firstSeen).toLocaleTimeString()}</span>
                        </div>
                      ) : '-'}
                    </td>
                    {/* Last Seen cell */}
                    <td className={`px-2 py-2 text-center border-l text-[9px] ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                      {endpoint.lastSeen ? (
                        <div className="flex flex-col items-center">
                          <span>{new Date(endpoint.lastSeen).toLocaleDateString()}</span>
                          <span className="text-[8px] opacity-75">{new Date(endpoint.lastSeen).toLocaleTimeString()}</span>
                        </div>
                      ) : '-'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className={`px-3 py-2 border-t flex items-center gap-4 text-[10px] flex-wrap ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <span className={`font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Legend:</span>
        <div className="flex items-center gap-1">
          <FontAwesomeIcon icon={faTimes} className="text-green-500" />
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Denied</span>
        </div>
        <div className="flex items-center gap-1">
          <FontAwesomeIcon icon={faCheckCircle} className="text-red-500" />
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Bypassed</span>
        </div>
        <div className="flex items-center gap-1">
          <FontAwesomeIcon icon={faQuestionCircle} className="text-yellow-500" />
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Review</span>
        </div>
        <div className="flex items-center gap-1">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-500" />
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Error</span>
        </div>
        {searchActive && contentMatches && contentMatches.size > 0 && (<>
          <span className={`ml-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>|</span>
          <span className={`font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Search:</span>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded" style={{ boxShadow: '0 0 0 1.5px rgba(250,204,21,0.9), 0 0 6px rgba(250,204,21,0.7)' }} />
            <span className={isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}>Response</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded" style={{ boxShadow: '0 0 0 1.5px rgba(168,85,247,0.9), 0 0 6px rgba(168,85,247,0.7)' }} />
            <span className={isDarkMode ? 'text-purple-400' : 'text-purple-600'}>Request</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded" style={{ boxShadow: '0 0 0 1.5px rgba(239,68,68,0.9), 0 0 6px rgba(239,68,68,0.7)' }} />
            <span className={isDarkMode ? 'text-red-400' : 'text-red-600'}>Both</span>
          </div>
        </>)}
      </div>

      {/* Right-click context menu */}
      {ctxMenu && (
        <div
          ref={ctxRef}
          className={`fixed z-[10001] rounded-lg shadow-xl border py-1 min-w-[180px] ${
            isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
          }`}
          style={{ top: ctxMenu.y, left: ctxMenu.x }}
        >
          <div className={`px-3 py-1.5 text-[10px] truncate max-w-[260px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {ctxMenu.label}
          </div>
          <div className={`border-t my-0.5 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
          <button
            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
              isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => { onSendToRepeater?.(ctxMenu.exchangeId); setCtxMenu(null); }}
          >
            <FontAwesomeIcon icon={faRedo} className="text-blue-400 w-3.5" />
            Send to Repeater
          </button>
          <button
            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
              isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => { onSendToIntruder?.(ctxMenu.exchangeId); setCtxMenu(null); }}
          >
            <FontAwesomeIcon icon={faCrosshairs} className="text-orange-400 w-3.5" />
            Send to Intruder
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthSmashMatrixTable;
